// Shared production vocabulary for the other UI redesign (handoff W15, W21–W23).
// The job read model and its blocker/risk DEFINITIONS live in the API
// (apps/api/src/services/production-jobs-rules.ts) — this file only labels
// them. Never re-derive a blocker here.

export type JobBlocker =
  | "exception"
  | "late"
  | "proof_with_us"
  | "not_sent"
  | "awaiting_ack"
  | "blanks_not_arrived"
  | "proof_rejected"
  | "proof_with_client"
  | "follow_up"
  | "in_hands_soon";

export type JobRisk = "late" | "at_risk" | "attention" | "on_track";

export interface ProductionJob {
  id: string;
  poNumber: number;
  poType: string;
  stage: string;
  vendorName: string;
  shipFromVendorName: string | null;
  orderId: string;
  orderNumber: number;
  projectId: string;
  projectNumber: number;
  projectName: string;
  clientId: string;
  clientName: string;
  ownerName: string | null;
  orderRepName: string | null;
  clientRepId: string | null;
  orderRepId: string | null;
  rush: boolean;
  firmInHands: boolean;
  productionStatus: "ok" | "follow_up_vendor" | "follow_up_rep" | "being_shipped" | null;
  inHandsDate: string | null;
  requiredShipDate: string | null;
  supplierShipDate: string | null;
  nextActionType: string | null;
  nextActionDate: string | null;
  exceptionFlag: boolean;
  exceptionReason: string | null;
  followUpFlag: boolean;
  sentAt: string | null;
  /** 0–3 = due to ship that many days from now with no tracking yet (server-derived). */
  dueToShipInDays?: number | null;
  vendorAcknowledgedAt: string | null;
  pairedPoId: string | null;
  pairedPoNumber: number | null;
  pairedPoStage: string | null;
  proofs: { withUs: number; withClient: number; rejected: number; approved: number; total: number };
  /** false = every logo is "No proof required" — no Proof sent box (Stephanie 2026-10-28). */
  proofNeeded?: boolean;
  /** The PO's internal notes — how many, and the newest (Stephanie 2026-10-16). */
  notes?: { count: number; latest: { body: string; at: string | null; by: string | null } | null };
  tracking: { status: string; carrier: string | null; trackingNumber: string | null; trackingUrl: string | null } | null;
  blockers: JobBlocker[];
  risk: JobRisk;
}

export interface ProductionJobsResponse {
  asOf: string;
  scope: "open" | "all";
  jobs: ProductionJob[];
  truncated: boolean;
}

/** What the blocker means, and the next step it asks for. */
export const BLOCKER_COPY: Record<JobBlocker, { label: string; next: string }> = {
  exception: { label: "Exception", next: "Resolve the exception" },
  late: { label: "Past ship-by date", next: "Chase the supplier for a ship date" },
  proof_with_us: { label: "Proof waiting on us", next: "Review the proof" },
  not_sent: { label: "Not sent", next: "Send the PO to the supplier" },
  awaiting_ack: { label: "Not confirmed", next: "Get the supplier to confirm" },
  blanks_not_arrived: { label: "Blanks not arrived", next: "Confirm the blanks reached the decorator" },
  proof_rejected: { label: "Proof rejected", next: "Get a revised proof" },
  proof_with_client: { label: "Proof with client", next: "Nudge the client on the proof" },
  follow_up: { label: "Follow-up", next: "Follow up" },
  in_hands_soon: { label: "In hands this week", next: "Confirm it arrives in time" },
};

/** Blockers that make a row read in the warn tone (always with a label). */
export const URGENT_BLOCKERS: ReadonlySet<JobBlocker> = new Set(["exception", "late", "blanks_not_arrived", "proof_rejected"]);

export const RISK_COPY: Record<JobRisk, string> = {
  late: "Late",
  at_risk: "At risk",
  attention: "To do",
  on_track: "On track",
};

export const PO_STAGE_LABEL: Record<string, string> = {
  draft: "Draft",
  sent: "Sent",
  confirmed: "Confirmed",
  in_production: "In production",
  shipped: "Shipped",
  delivered: "Delivered",
  billed: "Billed",
  closed: "Closed",
  cancelled: "Cancelled",
};

export function poStageLabel(s: string): string {
  return PO_STAGE_LABEL[s] ?? s.replace(/_/g, " ");
}

export type PillTone = "active" | "prospect" | "dormant" | "closed" | "warn" | "info";

/** One tone map for PO stages everywhere (list, record, dashboard, database). */
export function poStageTone(s: string): PillTone {
  switch (s) {
    case "draft":
      return "prospect";
    case "sent":
    case "confirmed":
    case "in_production":
    case "shipped":
      return "info";
    case "delivered":
    case "billed":
      return "active";
    case "closed":
      return "closed";
    case "cancelled":
      return "closed";
    default:
      return "info";
  }
}

export const PO_TYPE_LABEL: Record<string, string> = {
  product_supply: "Goods",
  decoration_service: "Decoration",
};

/** Blank-goods leg has arrived at the decorator (paired PO delivered or later). */
export function blanksArrived(pairedStage: string | null): boolean {
  return pairedStage === "delivered" || pairedStage === "billed" || pairedStage === "closed";
}

export const NEXT_ACTION_LABEL: Record<string, string> = {
  needs_art_approval: "Needs art approval",
  needs_data: "Needs data",
  // commonsku's list, in its order (Stephanie 2026-11-02).
  waiting_on_client_response: "Waiting on client response",
  firm_in_hands_date: "Firm in-hands date",
  call_vendor: "Call vendor",
  confirm_ship_date: "Confirm ship date",
  track_package: "Track package",
  confirm_order: "Confirm order",
};

export function formatShortDate(s: string | null, withYear = false): string {
  if (!s) return "—";
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s;
  return new Date(s.slice(0, 10) + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
  });
}

/** Whole days from today (local) to a YYYY-MM-DD date. */
export function daysFromToday(s: string | null): number | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(s.slice(0, 10) + "T00:00:00");
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

/** The staff PO record — nested in its project. */
export function poHref(j: { projectId: string; id: string }): string {
  return `/projects/${j.projectId}/pos/${j.id}`;
}

const RISK_ORDER: Record<JobRisk, number> = { late: 0, at_risk: 1, attention: 2, on_track: 3 };
const BLOCKER_ORDER: JobBlocker[] = [
  "exception",
  "late",
  "proof_with_us",
  "not_sent",
  "awaiting_ack",
  "blanks_not_arrived",
  "proof_rejected",
  "proof_with_client",
  "follow_up",
  "in_hands_soon",
];
export { BLOCKER_ORDER };

/** Most urgent first: risk, then the first blocker's priority, then in-hands. */
export function sortByUrgency(a: ProductionJob, b: ProductionJob): number {
  const r = RISK_ORDER[a.risk] - RISK_ORDER[b.risk];
  if (r !== 0) return r;
  const ba = a.blockers[0] ? BLOCKER_ORDER.indexOf(a.blockers[0]) : 99;
  const bb = b.blockers[0] ? BLOCKER_ORDER.indexOf(b.blockers[0]) : 99;
  if (ba !== bb) return ba - bb;
  return (a.inHandsDate ?? "9999").localeCompare(b.inHandsDate ?? "9999");
}
